import React from "react";

import { useTranslation } from "react-i18next";

import prettyDurations, { DURATION_UNITS } from "@/src/utils/durations";

import { formatShare } from "../charts/charts.utils";
import StatCard, { EMPTY_VALUE, formatCountOrEmpty } from "../kpi/StatCard";

import type { JSX } from "react";

import type { MediaKpi } from "@/src/api/StatsAPI.types";

import type { QueryStatus } from "../kpi/StatCard";

interface ContentKpiGridProps {
  mediaKpi: MediaKpi | undefined;
  mediaKpiStatus: QueryStatus;
  isLive: boolean;
  viewsInPeriod: number | undefined;
  viewsInPeriodStatus: QueryStatus;
  isAdmin: boolean;
}

const PERCENT_DIVISOR = 100;
const CATALOG_SHARE_FRACTION_DIGITS = 2;

function ContentKpiGrid({
  mediaKpi,
  mediaKpiStatus,
  isLive,
  viewsInPeriod,
  viewsInPeriodStatus,
  isAdmin,
}: ContentKpiGridProps): JSX.Element {
  const { t, i18n } = useTranslation("editor-analytics");
  const lang = i18n.language;
  const durationLabel = mediaKpi
    ? prettyDurations(mediaKpi.duration, {
        valueUnit: DURATION_UNITS.SECONDS,
        format: { noDays: true },
      }).humanised
    : "";

  return (
    <div className="grid grid-cols-2 gap-4 @3xl:grid-cols-4">
      <StatCard
        label={t(isLive ? "contentItem.kpi.sessions" : "contentItem.kpi.views")}
        query={mediaKpiStatus}
        scope="asOfToday"
        value={formatCountOrEmpty(mediaKpi?.views, lang)}
      />
      {isLive ? null : (
        <StatCard
          label={t("contentItem.kpi.views")}
          query={viewsInPeriodStatus}
          scope="period"
          value={formatCountOrEmpty(viewsInPeriod, lang)}
        />
      )}
      <StatCard
        label={t("contentItem.kpi.duration")}
        query={mediaKpiStatus}
        value={durationLabel || EMPTY_VALUE}
      />
      {isAdmin ? (
        <StatCard
          label={t("contentItem.kpi.catalogShare")}
          query={mediaKpiStatus}
          value={
            mediaKpi
              ? formatShare(
                  mediaKpi.catalog_view_percentage / PERCENT_DIVISOR,
                  lang,
                  CATALOG_SHARE_FRACTION_DIGITS,
                )
              : EMPTY_VALUE
          }
        />
      ) : null}
    </div>
  );
}

export default ContentKpiGrid;
